import { useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";

const campaign = {
  name: "Jason Hardcastle for Galveston City Council District 6",
  email: "info@votejasonhardcastle.org",
  location: "Galveston, TX, USA",
  facebookUrl: "https://www.facebook.com/profile.php?id=100068821106074",
};

const initialFormData = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

type ContactFormData = typeof initialFormData;

function ContactPage() {
  const [phoneConsent, setPhoneConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;

    setFormData((current) => {
      switch (name) {
        case "name": {
          return { ...current, name: value };
        }
        case "email": {
          return { ...current, email: value };
        }
        case "phone": {
          return { ...current, phone: value };
        }
        case "message": {
          return { ...current, message: value };
        }
        default: {
          return current;
        }
      }
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage("");

    if (!termsConsent) {
      alert("Please agree to the Terms and Conditions to continue.");
      return;
    }

    setIsSubmitting(true);

    try {
      const data = new URLSearchParams();
      for (const [key, value] of Object.entries(formData)) {
        data.append(key, value);
      }
      data.append("campaign", campaign.name);
      data.append("source", "contact-page");
      data.append("phoneConsent", phoneConsent.toString());
      data.append("termsConsent", termsConsent.toString());

      const response = await fetch("https://api.southcoaststrategy.com", {
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
      });

      if (!response.ok) {
        throw new Error("There was an error submitting your message. Please try again later.");
      }

      setIsSubmitted(true);
      setFormData(initialFormData);
      setPhoneConsent(false);
      setTermsConsent(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "An error occurred. Please try again later.";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitDisabled = isSubmitting || !termsConsent;
  const shouldShowForm = !isSubmitted;

  return (
    <main className="bg-white px-4 py-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <p className="font-bold uppercase tracking-[0.2em] text-[#c0352b]">Reach out to us</p>
          <h1 className="mt-3 text-5xl font-bold text-[#08213d]" style={{ fontFamily: "Libre Baskerville, serif" }}>
            Contact Jason&apos;s campaign
          </h1>
          <p className="mt-5 text-lg leading-8 text-[#37516a]">
            Send a message, request a sign, ask a question, or let the campaign know how you would like to help in District 6.
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="rounded-[2rem] bg-[#f7f1e6] p-8 shadow-xl">
            {shouldShowForm ? (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="mb-2 block font-bold text-[#08213d]">Name</label>
                  <input id="name" name="name" value={formData.name} onChange={handleInputChange} className="w-full rounded-xl border border-[#d7c9b8] bg-white p-4 text-[#112235] outline-none ring-[#0b6a88] transition focus:ring-2" />
                </div>

                <div>
                  <label htmlFor="email" className="mb-2 block font-bold text-[#08213d]">Email*</label>
                  <input id="email" name="email" type="email" required value={formData.email} onChange={handleInputChange} className="w-full rounded-xl border border-[#d7c9b8] bg-white p-4 text-[#112235] outline-none ring-[#0b6a88] transition focus:ring-2" />
                </div>

                <div>
                  <label htmlFor="phone" className="mb-2 block font-bold text-[#08213d]">Phone (optional)</label>
                  <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} className="w-full rounded-xl border border-[#d7c9b8] bg-white p-4 text-[#112235] outline-none ring-[#0b6a88] transition focus:ring-2" />
                </div>

                <div>
                  <label htmlFor="message" className="mb-2 block font-bold text-[#08213d]">Message</label>
                  <textarea id="message" name="message" rows={5} value={formData.message} onChange={handleInputChange} className="w-full rounded-xl border border-[#d7c9b8] bg-white p-4 text-[#112235] outline-none ring-[#0b6a88] transition focus:ring-2" />
                </div>

                <label className="flex gap-3 rounded-2xl bg-white p-4 text-sm leading-6 text-[#37516a]">
                  <input id="text-consent" name="text-consent" type="checkbox" checked={phoneConsent} onChange={() => setPhoneConsent((current) => !current)} className="mt-1 size-5 rounded border-[#0b2f58] text-[#0b2f58]" />
                  <span>
                    Optional SMS/MMS political text opt-in: By checking this box and submitting this form, I consent to receive recurring manually sent political text messages, including SMS and MMS, from Jason Hardcastle for Galveston City Council District 6. Message frequency varies. Message and data rates may apply. Reply STOP to opt out and HELP for help. Donations may be solicited. Consent is not a condition of donating, volunteering, or receiving campaign information. View our <Link to="/terms-and-conditions" className="font-bold text-[#0b2f58] underline">Terms and Conditions</Link> and <Link to="/privacy-policy" className="font-bold text-[#0b2f58] underline">Privacy Policy</Link>.
                  </span>
                </label>

                <label className="flex gap-3 rounded-2xl bg-white p-4 text-sm leading-6 text-[#37516a]">
                  <input id="terms-consent" name="terms-consent" type="checkbox" checked={termsConsent} onChange={() => setTermsConsent((current) => !current)} required className="mt-1 size-5 rounded border-[#0b2f58] text-[#0b2f58]" />
                  <span>
                    I agree to the <Link to="/terms-and-conditions" className="font-bold text-[#0b2f58] underline">Terms and Conditions</Link> and <Link to="/privacy-policy" className="font-bold text-[#0b2f58] underline">Privacy Policy</Link>.
                  </span>
                </label>

                <button type="submit" disabled={submitDisabled} className={`rounded-full px-8 py-4 text-sm font-extrabold uppercase tracking-[0.16em] shadow-lg transition ${submitDisabled ? "bg-gray-300 text-gray-500" : "bg-[#c0352b] text-white hover:bg-[#9f261f]"}`}>
                  {isSubmitting ? "Sending..." : "Send message"}
                </button>

                {errorMessage && <p className="font-semibold text-[#c0352b]">{errorMessage}</p>}

                <p className="text-xs leading-5 text-[#6a7b89]">
                  This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
                </p>
              </form>
            ) : (
              <div className="rounded-2xl bg-white p-8 text-center shadow-lg">
                <h2 className="text-3xl font-black text-[#08213d]">Message sent</h2>
                <p className="mt-4 text-[#37516a]">Thank you for reaching out. The campaign will be in touch shortly.</p>
                <button type="button" onClick={() => setIsSubmitted(false)} className="mt-7 rounded-full bg-[#0b2f58] px-7 py-3 text-sm font-extrabold uppercase tracking-[0.16em] text-white transition hover:bg-[#143f70]">
                  Send another message
                </button>
              </div>
            )}
          </section>

          <aside className="rounded-[2rem] bg-[#08213d] p-8 text-white shadow-xl">
            <h2 className="text-3xl font-bold" style={{ fontFamily: "Libre Baskerville, serif" }}>Campaign contact</h2>
            <div className="mt-7 space-y-5 text-white/85">
              <p>
                <span className="block text-sm font-bold uppercase tracking-[0.16em] text-[#f7d77a]">Email</span>
                <a href={`mailto:${campaign.email}`} className="text-xl font-bold text-white hover:text-[#f7d77a]">{campaign.email}</a>
              </p>
              <p>
                <span className="block text-sm font-bold uppercase tracking-[0.16em] text-[#f7d77a]">Location</span>
                {campaign.location}
              </p>
              <p>
                <span className="block text-sm font-bold uppercase tracking-[0.16em] text-[#f7d77a]">Social</span>
                <a href={campaign.facebookUrl} target="_blank" rel="noopener noreferrer" className="font-bold text-white hover:text-[#f7d77a]">Facebook campaign page</a>
              </p>
            </div>
            <div className="mt-10 rounded-2xl bg-white/10 p-6">
              <h3 className="text-xl font-black">Campaign donations</h3>
              <p className="mt-3 leading-7 text-white/75">
                Your support helps the campaign reach more voters across District 6.
              </p>
              <Link to="/donate" className="mt-5 inline-block rounded-full bg-[#f7d77a] px-6 py-3 text-sm font-extrabold uppercase tracking-[0.14em] text-[#08213d] transition hover:bg-white">
                Donate
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default ContactPage;
